import { useEffect, useRef } from 'react'

type Particle = {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  color: string
  size: number
  isRocket: boolean
  targetY?: number
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min)
}

function pickColor() {
  const colors = ['#ff3b30', '#ffcc00', '#34c759', '#0a84ff', '#bf5af2', '#ff9f0a', '#ff2d55']
  return colors[Math.floor(Math.random() * colors.length)]
}

export default function FireworksEffect() {
  const FIREWORKS_PER_WINDOW = 30
  const FIREWORK_WINDOW_MS = 5000
  const LAUNCH_INTERVAL_MS = Math.max(1, Math.floor(FIREWORK_WINDOW_MS / FIREWORKS_PER_WINDOW))

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const particlesRef = useRef<Particle[]>([])
  const intervalRef = useRef<number | null>(null)
  const lastTsRef = useRef<number>(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const setSize = () => {
      const dpr = Math.max(1, window.devicePixelRatio || 1)
      canvas.width = Math.floor(window.innerWidth * dpr)
      canvas.height = Math.floor(window.innerHeight * dpr)
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    setSize()
    window.addEventListener('resize', setSize)

    const launchRocket = () => {
      const w = window.innerWidth
      const h = window.innerHeight

      const x = rand(w * 0.15, w * 0.85)
      const y = h + 10
      const targetY = rand(h * 0.2, h * 0.45)

      const color = pickColor()

      const rocket: Particle = {
        x,
        y,
        vx: rand(-0.6, 0.6),
        vy: rand(-9.5, -12.5),
        life: 0,
        maxLife: 200,
        color,
        size: 2,
        isRocket: true,
        targetY,
      }

      particlesRef.current.push(rocket)
    }

    const explode = (x: number, y: number, color: string) => {
      const count = Math.floor(rand(16, 26))
      for (let i = 0; i < count; i++) {
        const angle = rand(0, Math.PI * 2)
        const speed = rand(2.2, 5.2)
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          maxLife: Math.floor(rand(55, 95)),
          color,
          size: rand(1.2, 2.4),
          isRocket: false,
        })
      }
    }

    launchRocket()
    if (intervalRef.current) window.clearInterval(intervalRef.current)
    intervalRef.current = window.setInterval(() => {
      launchRocket()
    }, LAUNCH_INTERVAL_MS)

    const step = (ts: number) => {
      const dt = Math.min(33, ts - (lastTsRef.current || ts)) / 16.67
      lastTsRef.current = ts

      const w = window.innerWidth
      const h = window.innerHeight

      ctx.clearRect(0, 0, w, h)
      ctx.globalCompositeOperation = 'lighter'

      const gravity = 0.18 * dt
      const drag = Math.pow(0.985, dt)

      const particles = particlesRef.current
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]
        p.life += 1 * dt

        if (p.isRocket) {
          p.vx *= drag
          p.vy = p.vy * drag + gravity * 0.6
          p.x += p.vx * dt
          p.y += p.vy * dt

          const shouldExplode = (typeof p.targetY === 'number' && p.y <= p.targetY) || p.vy >= -1.2
          if (shouldExplode) {
            explode(p.x, p.y, p.color)
            particles.splice(i, 1)
            continue
          }

          ctx.save()
          ctx.globalAlpha = 0.85
          ctx.strokeStyle = p.color
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(p.x, p.y)
          ctx.lineTo(p.x - p.vx * 2, p.y - p.vy * 2)
          ctx.stroke()
          ctx.restore()

          if (p.y > h + 40 || p.life > p.maxLife) {
            particles.splice(i, 1)
          }
          continue
        }

        p.vx *= drag
        p.vy = p.vy * drag + gravity
        p.x += p.vx * dt
        p.y += p.vy * dt

        const t = Math.max(0, 1 - p.life / p.maxLife)
        const alpha = t * t

        ctx.save()
        ctx.globalAlpha = alpha
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fill()
        ctx.restore()

        if (p.life >= p.maxLife || p.y > h + 60 || p.x < -60 || p.x > w + 60) {
          particles.splice(i, 1)
        }
      }

      ctx.globalCompositeOperation = 'source-over'

      rafRef.current = window.requestAnimationFrame(step)
    }

    rafRef.current = window.requestAnimationFrame(step)

    return () => {
      window.removeEventListener('resize', setSize)
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current)
      if (intervalRef.current) window.clearInterval(intervalRef.current)
      particlesRef.current = []
    }
  }, [LAUNCH_INTERVAL_MS])

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-[9998]" aria-hidden="true" />
}
